import React, { useState, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Grid,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import {
    Download as DownloadIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    People as PeopleIcon,
    TableChart as ExcelIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { supplierStore } from "../stores";
import Constants from "../constants";
import { exportToCsv, exportToExcel, formatDateForExcel } from "../utils/excelExport";
import { resolveStatsSupplierId } from "../utils/statsSupplier";
import { authStore } from "../stores";
import { statsStore } from "../stores";

interface PagesStatsProps {
    startDate?: string;
    endDate?: string;
    clientId?: number;
}

interface PageStat {
    pageUrl: string;
    clientName: string;
    viewCount: string;
    uniqueVisitors: string;
    firstView: string;
    lastView: string;
}

const PagesStats: React.FC<PagesStatsProps> = observer(({ startDate, endDate, clientId }) => {
    const [stats, setStats] = useState<PageStat[]>([]);
    const [totalViewCount, setTotalViewCount] = useState<number>(0);
    const [totalUniqueVisitorsCount, setTotalUniqueVisitorsCount] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchStats = async () => {
        const supplierId = resolveStatsSupplierId(supplierStore.currentSupplier, authStore.user);
        if (!supplierId || !startDate || !endDate) {
            return;
        }
        const requestKey = `${supplierId || "all"}|${clientId || "all"}|${startDate || ""}|${endDate || ""}`;
        if (statsStore.lastPageStatsKey === requestKey && statsStore.pageStats) {
            const cached = statsStore.pageStats as any;
            setStats(cached.data || []);
            setTotalViewCount(cached.totalViewCount || 0);
            setTotalUniqueVisitorsCount(
                cached.totalUniqueVisitors === undefined ? null : Number(cached.totalUniqueVisitors),
            );
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await statsStore.fetchPageStats(clientId ?? null, new Date(startDate), new Date(endDate), supplierId);
            const cached = statsStore.pageStats as any;
            setStats(Array.isArray(cached?.data) ? cached.data : []);
            setTotalViewCount(cached?.totalViewCount || 0);
            setTotalUniqueVisitorsCount(
                cached?.totalUniqueVisitors === undefined ? null : Number(cached.totalUniqueVisitors),
            );
        } catch (err) {
            setError("Ошибка загрузки статистики страниц");
            console.error("Error fetching pages stats:", err);
        } finally {
            setLoading(false);
        }
    };

    const exportToCSV = async () => {
        try {
            // Создаем CSV из уже загруженных данных
            if (!stats || stats.length === 0) {
                setError("Нет данных для экспорта");
                return;
            }

            const csvHeaders = [
                "URL страницы",
                "Клиент",
                "Просмотры",
                "Зрители",
                "Первый просмотр",
                "Последний просмотр",
            ];
            const csvRows = stats.map((item: any) => [
                item.pageUrl || "",
                item.clientName || "",
                item.viewCount || "0",
                item.uniqueVisitors || "0",
                item.firstView ? new Date(item.firstView).toLocaleString("ru-RU") : "",
                item.lastView ? new Date(item.lastView).toLocaleString("ru-RU") : "",
            ]);

            exportToCsv({
                filename: `page_stats_${new Date().toISOString().split("T")[0]}`,
                headers: csvHeaders,
                data: csvRows,
                separator: Constants.CSV_SEPARATOR,
            });
        } catch (err) {
            setError("Ошибка экспорта данных");
            console.error("Error exporting pages stats:", err);
        }
    };

    const exportToExcelPages = async () => {
        try {
            if (!stats || stats.length === 0) {
                setError("Нет данных для экспорта");
                return;
            }

            const headers = [
                "URL страницы",
                "Клиент",
                "Просмотры",
                "Зрители",
                "Первый просмотр",
                "Последний просмотр",
            ];

            const data = stats.map((item: any) => [
                item.pageUrl || "",
                item.clientName || "",
                item.viewCount || "0",
                item.uniqueVisitors || "0",
                item.firstView ? new Date(item.firstView).toLocaleString("ru-RU") : "",
                item.lastView ? new Date(item.lastView).toLocaleString("ru-RU") : "",
            ]);

            const additionalInfo = [
                { label: "Период", value: `${formatDateForExcel(new Date(startDate || ""))} - ${formatDateForExcel(new Date(endDate || ""))}` },
                { label: "Поставщик", value: supplierStore.currentSupplier?.name || "Не выбран" },
                { label: "Всего записей", value: stats.length },
                { label: "Общее количество просмотров", value: totalViewCount },
                { label: "Зрителей", value: totalUniqueVisitors }
            ];

            exportToExcel({
                filename: `page_stats_${new Date().toISOString().split("T")[0]}`,
                sheetName: "Статистика страниц",
                headers,
                data,
                additionalInfo
            });
        } catch (error) {
            console.error("Ошибка экспорта в Excel:", error);
            setError("Ошибка экспорта в Excel");
        }
    };

    useEffect(() => {
        fetchStats();
    }, [startDate, endDate, clientId, supplierStore.currentSupplier?.id]);

    // Используем totalViewCount из API если есть, иначе суммируем TOP 20
    const totalViews = totalViewCount || stats.reduce((sum, stat) => sum + Number(stat.viewCount), 0);
    const totalUniqueVisitors =
        totalUniqueVisitorsCount ?? stats.reduce((sum, stat) => sum + Number(stat.uniqueVisitors), 0);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h2">
                    Статистика страниц
                </Typography>
                <Box>
                    {/* <Tooltip title="Обновить данные"> */}
                    <IconButton onClick={fetchStats} color="primary">
                        <RefreshIcon />
                    </IconButton>
                    {/* </Tooltip> */}
                    <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportToCSV} sx={{ ml: 1 }}>
                        Экспорт CSV
                    </Button>
                    <Button 
                        variant="contained" 
                        color="success" 
                        startIcon={<ExcelIcon />} 
                        onClick={exportToExcelPages} 
                        sx={{ ml: 1 }}
                    >
                        Экспорт Excel
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Сводная статистика */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <VisibilityIcon color="primary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography variant="h4" component="div">
                                        {totalViews.toLocaleString()}
                                    </Typography>
                                    <Typography color="textSecondary">Всего просмотров</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <PeopleIcon color="secondary" sx={{ mr: 1 }} />
                                <Box>
                                    <Typography variant="h4" component="div">
                                        {totalUniqueVisitors.toLocaleString()}
                                    </Typography>
                                    <Typography color="textSecondary">Зрителей</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Typography variant="h4" component="div">
                                    {stats.length}
                                </Typography>
                                <Box ml={1}>
                                    <Typography color="textSecondary">Уникальных страниц</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center">
                                <Typography variant="h4" component="div">
                                    {totalViews > 0 ? (totalViews / stats.length).toFixed(1) : "0"}
                                </Typography>
                                <Box ml={1}>
                                    <Typography color="textSecondary">Среднее просмотров на страницу</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Информационный баннер с пояснением - скрыт для поставщиков */}
            {/* <Alert severity="info" sx={{ mb: 3, mx: 2 }}>
          <Typography variant="body2">
            <strong>Статистика страниц:</strong> Показывает все просмотры
            страниц (HTML-страницы, каталоги фильмов/сериалов) на устройствах
            abox за выбранный период. Зрители могут пересекаться с другими
            вкладками (один пользователь может смотреть и страницы, и медиа).
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>URL страниц:</strong> Показывают структуру контента по
            сериалам/фильмам. Например, <code>/series/vyzhivshie/</code>{" "}
            означает просмотры страницы сериала &quot;Выжившие&quot;. Это
            помогает понять, какой контент наиболее популярен у зрителей.
          </Typography>
        </Alert> */}

            {/* Таблица статистики */}
            <Card>
                <CardContent>
                    <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Страница</TableCell>
                                    <TableCell>Клиент</TableCell>
                                    <TableCell align="right">Просмотры</TableCell>
                                    <TableCell align="right">Зрители</TableCell>
                                    <TableCell>Первый просмотр</TableCell>
                                    <TableCell>Последний просмотр</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stats.map((stat, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ maxWidth: 300, wordBreak: "break-all" }}>
                                                {stat.pageUrl}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={stat.clientName}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" color="primary">
                                                {Number(stat.viewCount).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body1">
                                                {Number(stat.uniqueVisitors).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {new Date(stat.firstView).toLocaleDateString("ru-RU")}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="textSecondary">
                                                {new Date(stat.lastView).toLocaleDateString("ru-RU")}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
});

export default PagesStats;
